// src/admin/pages/import-orders/ImportOrderItemsEditor.tsx
//
// Shared item-entry table used by both ImportOrderCreateDialog and
// ImportOrderEditDialog. Replaces the old "pick an existing catalog Product,
// then enter a quantity" picker — an import order is how a car ENTERS the
// catalog, so items now carry the vehicle spec (brand/model/trim/category)
// directly. Each item is also tagged with an acquisitionType, since a
// brand-new batch and a single used vehicle need different fields (see
// answers.text and ImportOrder.ts).
import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Box,
  Autocomplete, TextField, MenuItem, IconButton, Button, Stack, ToggleButtonGroup,
  ToggleButton, Grid, Chip, Collapse, Divider,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { ImportOrderItem, AcquisitionType } from './ImportOrder';
import { Brand, fetchActiveBrands } from '../brand/brandService';
import { CarModel } from '../carmodel/carModel';
import { fetchActiveCarModels } from '../carmodel/carModelService';
import { CategoryService } from '../category/categoryService';
import { Category, CAR_CONDITIONS } from '../../../enums/product';
import { formatCurrency } from '../../../util/Formatcurrency';

const emptyItem = (): ImportOrderItem => ({
  id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  acquisitionType: 'brand_new_batch',
  brandId: '', brandName: '',
  modelId: '', modelName: '',
  trimId: '', trimName: '',
  categoryId: '', categoryName: '',
  quantity: 1, unitPrice: 0, totalPrice: 0, receivedQuantity: 0,
});

interface ImportOrderItemsEditorProps {
  items: ImportOrderItem[];
  setItems: React.Dispatch<React.SetStateAction<ImportOrderItem[]>>;
  currencySymbol: string;
  disabled?: boolean;
}

const ImportOrderItemsEditor: React.FC<ImportOrderItemsEditorProps> = ({
  items, setItems, currencySymbol, disabled = false,
}) => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [carModels, setCarModels] = useState<CarModel[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  // Cards default expanded (nothing changes visually for orders with few
  // items) — collapsing is purely an opt-in way to skim a long order.
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const toggleCollapsed = (id: string) => setCollapsedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  useEffect(() => {
    (async () => {
      try {
        const [b, m, c] = await Promise.all([
          fetchActiveBrands(),
          fetchActiveCarModels(),
          CategoryService.getMainCategories(),
        ]);
        setBrands(b);
        setCarModels(m);
        setCategories(c);
      } catch (err) {
        console.error('Failed to load brand/model/category reference data:', err);
      }
    })();
  }, []);

  const handleAddItem = () => setItems(prev => [...prev, emptyItem()]);
  const handleRemoveItem = (index: number) => setItems(prev => prev.filter((_, i) => i !== index));

  const updateItem = (index: number, patch: Partial<ImportOrderItem>) => {
    setItems(prev => {
      const next = [...prev];
      const updated = { ...next[index], ...patch };
      updated.totalPrice = updated.quantity * updated.unitPrice;
      next[index] = updated;
      return next;
    });
  };

  const handleAcquisitionTypeChange = (index: number, type: AcquisitionType) => {
    // Used/individual is always exactly one vehicle — lock quantity to 1 and
    // clear whichever type-specific fields no longer apply.
    updateItem(index, type === 'used_individual'
      ? { acquisitionType: type, quantity: 1 }
      : { acquisitionType: type, vin: '', condition: '', modelYear: undefined, mileageKm: undefined, exteriorColor: '', inspectionNotes: '' }
    );
  };

  const handleBrandChange = (index: number, brand: Brand | null) => {
    updateItem(index, {
      brandId: brand?.id || '', brandName: brand?.name || '',
      modelId: '', modelName: '', trimId: '', trimName: '',
    });
  };

  const handleModelChange = (index: number, model: CarModel | null) => {
    updateItem(index, {
      modelId: model?.id || '', modelName: model?.name || '',
      trimId: '', trimName: '',
    });
  };

  const handleTrimChange = (index: number, trimId: string) => {
    const item = items[index];
    const model = carModels.find(m => m.id === item.modelId);
    const trim = model?.trims.find(t => t.id === trimId);
    updateItem(index, { trimId, trimName: trim?.name || '' });
  };

  const modelsForBrand = (brandId: string) => carModels.filter(m => m.brandId === brandId);
  const trimsForModel = (modelId: string) => carModels.find(m => m.id === modelId)?.trims.filter(t => t.isActive) || [];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Order Items</Typography>
        <Button startIcon={<AddIcon />} onClick={handleAddItem} size="small" variant="outlined" disabled={disabled}>
          Add Item
        </Button>
      </Stack>

      {items.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed', bgcolor: 'action.hover' }}
        >
          <Typography variant="body2" color="text.secondary">
            No items added. Click "Add Item" to get started.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {items.map((item, index) => {
            const isCollapsed = collapsedIds.has(item.id!);
            const summary = `${item.brandName || 'Brand'} ${item.modelName || 'Model'}${item.trimName ? ' ' + item.trimName : ''} · ${item.acquisitionType === 'used_individual' ? 'Used' : 'Brand New'} · Qty ${item.quantity} · ${formatCurrency(item.totalPrice, currencySymbol)}`;

            return (
              <Paper key={item.id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: isCollapsed ? 0 : 1.5 }}>
                  <IconButton size="small" onClick={() => toggleCollapsed(item.id!)}>
                    {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                  </IconButton>
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }} noWrap>
                    {summary}
                  </Typography>
                  <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)} disabled={disabled}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>

                <Collapse in={!isCollapsed}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={4} md={2}>
                      <ToggleButtonGroup
                        size="small" exclusive value={item.acquisitionType} fullWidth
                        onChange={(_, v) => v && handleAcquisitionTypeChange(index, v)}
                        disabled={disabled}
                      >
                        <ToggleButton value="brand_new_batch" sx={{ fontSize: '0.7rem', px: 1 }}>Brand New</ToggleButton>
                        <ToggleButton value="used_individual" sx={{ fontSize: '0.7rem', px: 1 }}>Used</ToggleButton>
                      </ToggleButtonGroup>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                      <Autocomplete
                        options={brands}
                        getOptionLabel={(o) => o.name}
                        value={brands.find(b => b.id === item.brandId) || null}
                        onChange={(_, v) => handleBrandChange(index, v)}
                        renderInput={(params) => <TextField {...params} size="small" label="Brand" />}
                        disabled={disabled}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                      <Autocomplete
                        options={modelsForBrand(item.brandId)}
                        getOptionLabel={(o) => o.name}
                        value={carModels.find(m => m.id === item.modelId) || null}
                        onChange={(_, v) => handleModelChange(index, v)}
                        renderInput={(params) => <TextField {...params} size="small" label="Model" />}
                        disabled={disabled || !item.brandId}
                      />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <TextField
                        fullWidth select size="small" label="Trim" value={item.trimId || ''}
                        onChange={(e) => handleTrimChange(index, e.target.value)}
                        disabled={disabled || !item.modelId || trimsForModel(item.modelId).length === 0}
                      >
                        <MenuItem value="">—</MenuItem>
                        {trimsForModel(item.modelId).map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <TextField
                        fullWidth select size="small" label="Body Type" value={item.categoryId}
                        onChange={(e) => {
                          const cat = categories.find(c => c.id === e.target.value);
                          updateItem(index, { categoryId: e.target.value, categoryName: cat?.name || '' });
                        }}
                        disabled={disabled}
                      >
                        {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                      </TextField>
                    </Grid>

                    {/* Qty / Unit Cost get their own row with generous width — these
                        are the financial figures on this card and were previously
                        squeezed into a 1/12-width column each. */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 0.5 }} />
                    </Grid>
                    <Grid item xs={4} sm={3} md={2}>
                      <TextField
                        fullWidth type="number" size="small" label="Qty" value={item.quantity}
                        onChange={(e) => updateItem(index, { quantity: parseInt(e.target.value) || 0 })}
                        inputProps={{ min: 1 }}
                        disabled={disabled || item.acquisitionType === 'used_individual'}
                      />
                    </Grid>
                    <Grid item xs={8} sm={5} md={4}>
                      <TextField
                        fullWidth type="number" size="small" label={`Unit Cost (${currencySymbol})`} value={item.unitPrice}
                        onChange={(e) => updateItem(index, { unitPrice: parseFloat(e.target.value) || 0 })}
                        inputProps={{ min: 0, step: 0.01 }} disabled={disabled}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Line Total: {formatCurrency(item.totalPrice, currencySymbol)}
                      </Typography>
                    </Grid>

                    {item.acquisitionType === 'used_individual' && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Chip size="small" color="info" label="Used vehicle details" />
                        </Stack>
                        <Grid container spacing={1.5}>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              fullWidth size="small" label="VIN / Chassis (if known)"
                              value={item.vin || ''} onChange={(e) => updateItem(index, { vin: e.target.value })}
                              disabled={disabled}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              fullWidth select size="small" label="Condition"
                              value={item.condition || ''} onChange={(e) => updateItem(index, { condition: e.target.value })}
                              disabled={disabled}
                            >
                              {CAR_CONDITIONS.filter(c => c !== 'Brand New').map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <TextField
                              fullWidth size="small" label="Model Year" type="number"
                              value={item.modelYear ?? ''} onChange={(e) => updateItem(index, { modelYear: parseInt(e.target.value) || undefined })}
                              disabled={disabled}
                            />
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <TextField
                              fullWidth size="small" label="Mileage (km)" type="number"
                              value={item.mileageKm ?? ''} onChange={(e) => updateItem(index, { mileageKm: parseInt(e.target.value) || undefined })}
                              disabled={disabled}
                            />
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <TextField
                              fullWidth size="small" label="Exterior Color"
                              value={item.exteriorColor || ''} onChange={(e) => updateItem(index, { exteriorColor: e.target.value })}
                              disabled={disabled}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth size="small" label="Inspection / Auction Sheet Notes" multiline rows={2}
                              value={item.inspectionNotes || ''} onChange={(e) => updateItem(index, { inspectionNotes: e.target.value })}
                              disabled={disabled}
                              placeholder="Condition grade, accident/flood disclosure, auction sheet reference, etc."
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Collapse>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export const validateImportOrderItems = (items: ImportOrderItem[]): string | null => {
  if (items.length === 0) return 'Please add at least one item';
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!item.brandId || !item.modelId) return `Please select a brand and model for item #${i + 1}`;
    if (!item.categoryId) return `Please select a body type for item #${i + 1}`;
    if (item.quantity <= 0) return `Please enter a valid quantity for item #${i + 1}`;
    if (item.unitPrice <= 0) return `Please enter a valid unit cost for item #${i + 1}`;
    if (item.acquisitionType === 'used_individual' && item.quantity !== 1) {
      return `A used/individual item must have quantity 1 (item #${i + 1})`;
    }
  }
  return null;
};

export default ImportOrderItemsEditor;
